import { Input, Label, Select, Switch } from '@quickmd-llc/design-system'
import { EditMemberImage } from '@/components/edit-member-image/EditMemberImage'
import { medicalProfessionOptions, prefixOptions, roleOptions, suffixOptions } from '@/utils/commonMembersPage'
import { setPageStatus } from '@/store/newMember/newMemberActions'

/** Field keys forwarded to the orchestrator's `onSetFormValues` handler. */
export type FormFieldType = 'email' | 'firstName' | 'lastName' | 'role' | 'ProfessionalTitle' | 'phoneNumber'

type Props = {
  role: string

  profileImgInputValue: string | Blob
  setProfileImgInputValue: (value: string | Blob) => void
  isImageChanged: boolean
  setIsImageChanged: (value: boolean) => void

  roleError: boolean
  roleLocalError: string
  onChangeRole: (value: string) => void

  prefix: string
  setPrefix: (value: string) => void
  suffix: string
  setSuffix: (value: string) => void

  firstName: string
  firstNameError: boolean
  firstNameFieldError: string

  lastName: string
  lastNameError: boolean
  lastNameFieldError: string

  email: string
  emailError: boolean
  emailFieldError: string

  phoneNumber: string
  phoneLocalError: string
  isErrorPhoneNumberInput: boolean
  onPhoneBlur: () => void

  professionalTitle: string
  professionalTitleError: boolean
  professionalTitleLocalError: string

  npi: string
  setNpi: (value: string) => void

  isExcludedFromMarketingReports: boolean
  setIsExcludedFromMarketingReports: (value: boolean) => void

  onSetFormValues: (type: FormFieldType, value: string) => void
}

/**
 * "General information" card: profile photo + identity fields (role, prefix/first name,
 * last name/suffix, email, provider-only phone / professional title / NPI / exclude switch).
 *
 * SCSS → Tailwind mapping ($border-color = var(--border-card) = shade[10] = #141E1F1A;
 * --text-dark = text.dark; --text-medium ≈ text.light; --text-error = text.error;
 * --surface-secondary = deep-navy-5):
 *   .nm-card        -> bg-white border border-shade-10 rounded-large
 *   .nm-card-header -> py-sp-2 px-sp-3 border-b border-shade-10
 *   .nm-card-title  -> text-size-4 font-semibold leading-6 text-text-dark
 *   .nm-card-body   -> p-sp-3
 *   .nm-body-layout -> flex gap-sp-4 items-start
 *   .nm-photo-col   -> shrink-0 flex items-start
 *   .nm-fields-col  -> flex-1 min-w-0 flex flex-col gap-sp-2
 *   .nm-field       -> flex flex-col gap-sp-0.75
 *   .nm-field-error -> text-size-2 leading-4 text-text-error
 *   .nm-row         -> grid grid-cols-[160px_1fr] gap-sp-2 [&>*]:min-w-0
 *   .nm-row--last-suffix -> grid-cols-[1fr_160px]
 *   .nm-row--half        -> grid-cols-[1fr_1fr]
 *   .nm-phone-wrapper(--simple) -> flex items-stretch; prefix box + radius-stripped input
 *   .nm-phone-prefix -> flex items-center justify-center px-sp-1.5 border border-shade-10
 *                       border-r-0 rounded-l-[6px] bg-white text-size-3 text-text-dark
 *                       whitespace-nowrap shrink-0 (error → border-text-error)
 *                       (SCSS left radius was 6px; no DS token = 6px → arbitrary rounded-l-[6px])
 *   .nm-phone-number-input -> flex-1 + rounded-l-none on the input
 *   .nm-switch-row   -> flex items-center justify-between gap-sp-1.5 py-sp-0.5
 *   .nm-switch-label -> text-size-3 leading-5 text-text-dark
 *   .nm-switch-right -> flex items-center gap-sp-1
 *   .nm-switch-value -> text-size-3 text-text-light min-w-sp-3 text-right
 */
export const GeneralInformationSection = ({
  role,
  profileImgInputValue,
  setProfileImgInputValue,
  isImageChanged,
  setIsImageChanged,
  roleError,
  roleLocalError,
  onChangeRole,
  prefix,
  setPrefix,
  suffix,
  setSuffix,
  firstName,
  firstNameError,
  firstNameFieldError,
  lastName,
  lastNameError,
  lastNameFieldError,
  email,
  emailError,
  emailFieldError,
  phoneNumber,
  phoneLocalError,
  isErrorPhoneNumberInput,
  onPhoneBlur,
  professionalTitle,
  professionalTitleError,
  professionalTitleLocalError,
  npi,
  setNpi,
  isExcludedFromMarketingReports,
  setIsExcludedFromMarketingReports,
  onSetFormValues,
}: Props) => {
  const emailField = (
    <div className='flex flex-col gap-sp-0.75'>
      <Label>Email*</Label>
      <Input
        value={email}
        onChange={(e) => onSetFormValues('email', e.target.value)}
        error={emailError}
        placeholder='Email address'
        type='email'
        data-testid='pw_nm-email'
      />
      {emailFieldError ?
        <span className='text-size-2 leading-4 text-text-error'>{emailFieldError}</span>
      : null}
    </div>
  )

  return (
    <div className='rounded-large border border-shade-10 bg-white'>
      <div className='border-b border-shade-10 px-sp-3 py-sp-2'>
        <span className='text-size-4 font-semibold leading-6 text-text-dark'>General information</span>
      </div>
      <div className='p-sp-3'>
        <div className='flex items-start gap-sp-4'>
          {/* Photo column */}
          <div className='flex shrink-0 items-start'>
            <EditMemberImage
              isVisible
              isImageChanged={isImageChanged}
              profileImgInputValue={profileImgInputValue}
              setProfileImgInputValue={setProfileImgInputValue}
              setPageStatus={setPageStatus}
              setIsImageChanged={setIsImageChanged}
              style={{ marginRight: 0 }}
            />
          </div>

          {/* Fields column */}
          <div className='flex min-w-0 flex-1 flex-col gap-sp-2'>
            {/* Role */}
            <div className='flex flex-col gap-sp-0.75'>
              <Label>Role*</Label>
              <Select
                value={role}
                onChange={(e) => onChangeRole(e.target.value)}
                error={roleError}
                data-testid='pw_nm-role-select'>
                <option value=''>Select role</option>
                {roleOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
              {roleLocalError ?
                <span className='text-size-2 leading-4 text-text-error'>{roleLocalError}</span>
              : null}
            </div>

            {/* Prefix | First name */}
            <div className='grid grid-cols-[160px_1fr] gap-sp-2 [&>*]:min-w-0'>
              <div className='flex flex-col gap-sp-0.75'>
                <Label>Prefix</Label>
                <Select value={prefix} onChange={(e) => setPrefix(e.target.value)}>
                  {prefixOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt || '—'}
                    </option>
                  ))}
                </Select>
              </div>
              <div className='flex flex-col gap-sp-0.75'>
                <Label>First name*</Label>
                <Input
                  value={firstName}
                  onChange={(e) => onSetFormValues('firstName', e.target.value)}
                  error={firstNameError}
                  placeholder='First name'
                  data-testid='pw_nm-first-name'
                />
                {firstNameFieldError ?
                  <span className='text-size-2 leading-4 text-text-error'>{firstNameFieldError}</span>
                : null}
              </div>
            </div>

            {/* Last name | Suffix */}
            <div className='grid grid-cols-[1fr_160px] gap-sp-2 [&>*]:min-w-0'>
              <div className='flex flex-col gap-sp-0.75'>
                <Label>Last name*</Label>
                <Input
                  value={lastName}
                  onChange={(e) => onSetFormValues('lastName', e.target.value)}
                  error={lastNameError}
                  placeholder='Last name'
                  data-testid='pw_nm-last-name'
                />
                {lastNameFieldError ?
                  <span className='text-size-2 leading-4 text-text-error'>{lastNameFieldError}</span>
                : null}
              </div>
              <div className='flex flex-col gap-sp-0.75'>
                <Label>Suffix</Label>
                <Select value={suffix} onChange={(e) => setSuffix(e.target.value)}>
                  {suffixOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt || '—'}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Email | Phone (provider) or Email alone (admin) */}
            {role === 'Provider' ?
              <div className='grid grid-cols-[1fr_1fr] gap-sp-2 [&>*]:min-w-0'>
                {emailField}
                <div className='flex flex-col gap-sp-0.75'>
                  <Label>Phone number</Label>
                  <div className='flex items-stretch'>
                    <span
                      className={`flex shrink-0 items-center justify-center whitespace-nowrap rounded-l-[6px] border border-r-0 bg-white px-sp-1.5 text-size-3 text-text-dark ${
                        isErrorPhoneNumberInput ? 'border-text-error' : 'border-shade-10'
                      }`}>
                      +1
                    </span>
                    <Input
                      className='flex-1 rounded-l-none'
                      value={phoneNumber}
                      onChange={(e) => onSetFormValues('phoneNumber', e.target.value)}
                      onBlur={onPhoneBlur}
                      error={isErrorPhoneNumberInput}
                      placeholder='Phone number'
                      inputMode='numeric'
                    />
                  </div>
                  {phoneLocalError ?
                    <span className='text-size-2 leading-4 text-text-error'>{phoneLocalError}</span>
                  : null}
                </div>
              </div>
            : emailField}

            {/* Provider-only: Professional title | NPI + exclude switch */}
            {role === 'Provider' && (
              <>
                <div className='grid grid-cols-[1fr_1fr] gap-sp-2 [&>*]:min-w-0'>
                  <div className='flex flex-col gap-sp-0.75'>
                    <Label>Professional title*</Label>
                    <Select
                      value={professionalTitle}
                      onChange={(e) => onSetFormValues('ProfessionalTitle', e.target.value)}
                      error={professionalTitleError}>
                      <option value=''>Select professional title</option>
                      {medicalProfessionOptions.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Select>
                    {professionalTitleLocalError ?
                      <span className='text-size-2 leading-4 text-text-error'>{professionalTitleLocalError}</span>
                    : null}
                  </div>
                  <div className='flex flex-col gap-sp-0.75'>
                    <Label>NPI</Label>
                    <Input
                      value={npi}
                      onChange={(e) => setNpi(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder='NPI number'
                      inputMode='numeric'
                    />
                  </div>
                </div>

                {/* Exclude from reporting */}
                <div className='flex items-center justify-between gap-sp-1.5 py-sp-0.5'>
                  <span className='text-size-3 leading-5 text-text-dark'>Exclude from reporting</span>
                  <div className='flex items-center gap-sp-1'>
                    <span className='min-w-sp-3 text-right text-size-3 text-text-light'>
                      {isExcludedFromMarketingReports ? 'Yes' : 'No'}
                    </span>
                    <Switch
                      checked={isExcludedFromMarketingReports}
                      onChange={(v) => setIsExcludedFromMarketingReports(v)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
